import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle } from "lucide-react";
import { subscribeNewsletter } from "../../services/api";
import { useLocation } from "react-router-dom";
import {
  NEPAL_RELIEF,
  NEPAL_RELIEF_DISMISS_KEY,
  NEPAL_RELIEF_EVENT,
} from "../../data/nepalRelief";

const NewsletterPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const location = useLocation();
  const [reliefOpen, setReliefOpen] = useState(
    () => NEPAL_RELIEF.active && !sessionStorage.getItem(NEPAL_RELIEF_DISMISS_KEY)
  );

  // Queue behind the Together for Nepal popup instead of opening on top of it.
  useEffect(() => {
    if (!reliefOpen) return undefined;
    const onReliefDismissed = () => setReliefOpen(false);
    window.addEventListener(NEPAL_RELIEF_EVENT, onReliefDismissed);
    return () => window.removeEventListener(NEPAL_RELIEF_EVENT, onReliefDismissed);
  }, [reliefOpen]);

  useEffect(() => {
    // The homepage has a dedicated 2026 tour announcement.
    if (location.pathname === "/") return;
    if (reliefOpen) return;
    const dismissed = localStorage.getItem("newsletter_dismissed");
    if (dismissed) return;

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [location.pathname, reliefOpen]);

  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem("newsletter_dismissed", "true");
  };

  useEffect(() => {
    if (!isVisible) return undefined;
    const closeOnEscape = (event) => event.key === "Escape" && handleClose();
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [isVisible]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    try {
      await subscribeNewsletter({ name, email, source: "popup" });
      setStatus("success");
      localStorage.setItem("newsletter_dismissed", "true");

      setTimeout(() => {
        setIsVisible(false);
      }, 3000);
    } catch (err) {
      setStatus("error");
      const msg = err.message?.includes("JSON")
        ? "Subscription service unavailable. Try again after deployment."
        : err.message || "Something went wrong. Please try again.";
      setErrorMsg(msg);
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="newsletter-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={handleClose}
        >
          <motion.div
            className="newsletter-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="newsletter-title"
            initial={{ opacity: 0, scale: 0.9, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 40 }}
            transition={{ type: "spring", damping: 25, stiffness: 400 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="newsletter-close"
              onClick={handleClose}
              aria-label="Close newsletter signup"
            >
              <X size={16} />
            </button>

            {status === "success" ? (
              <div className="newsletter-success" role="status" aria-live="polite">
                <div className="success-icon">
                  <CheckCircle size={48} aria-hidden="true" />
                </div>
                <h3 id="newsletter-title">You're In!</h3>
                <p>Thanks for subscribing. We'll keep you updated on upcoming events!</p>
              </div>
            ) : (
              <>
                <div className="newsletter-header">
                  <h3 id="newsletter-title">Never Miss an Event</h3>
                  <p>
                    Subscribe to get exclusive updates on upcoming concerts,
                    festivals, and cultural celebrations.
                  </p>
                </div>

                <form className="newsletter-form" onSubmit={handleSubmit}>
                  <div className="form-field">
                    <label className="sr-only" htmlFor="newsletter-name">
                      Your name
                    </label>
                    <input
                      id="newsletter-name"
                      type="text"
                      autoComplete="name"
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="sr-only" htmlFor="newsletter-email">
                      Your email
                    </label>
                    <input
                      id="newsletter-email"
                      type="email"
                      autoComplete="email"
                      placeholder="Your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div role="alert" aria-live="assertive">
                    {status === "error" && (
                      <div className="newsletter-error">{errorMsg}</div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="newsletter-submit"
                    disabled={status === "loading"}
                  >
                    {status === "loading" ? (
                      <span className="loading-dots">Subscribing...</span>
                    ) : (
                      "Subscribe Now"
                    )}
                  </button>

                  <p className="newsletter-privacy">
                    We respect your privacy. Unsubscribe anytime.
                  </p>
                </form>
              </>
            )}
          </motion.div>

          <style>{`
            .newsletter-overlay {
              position: fixed;
              inset: 0;
              background: rgba(0, 0, 0, 0.7);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 10000;
              padding: 1rem;
            }

            .newsletter-modal {
              position: relative;
              background: #1a1a1a;
              border: 1px solid #333;
              border-radius: 12px;
              padding: 3rem;
              max-width: 440px;
              width: min(100%, 440px);
              box-sizing: border-box;
              box-shadow: 0 25px 60px rgba(0, 0, 0, 0.5);
            }

            .newsletter-close {
              position: absolute;
              top: 1rem;
              right: 1rem;
              background: rgba(255, 255, 255, 0.08);
              border: 1px solid rgba(255, 255, 255, 0.1);
              color: rgba(255, 255, 255, 0.6);
              width: 36px;
              height: 36px;
              border-radius: 8px;
              cursor: pointer;
              font-size: 0.85rem;
              display: flex;
              align-items: center;
              justify-content: center;
              transition: all 0.2s;
            }

            .newsletter-close:hover {
              background: rgba(255, 255, 255, 0.15);
              color: white;
            }

            .newsletter-header {
              text-align: center;
              margin-bottom: 2rem;
            }


            .newsletter-header h3 {
              color: white;
              font-size: 1.65rem;
              font-weight: 900;
              margin: 0 0 0.75rem;
              line-height: 1.2;
            }

            .newsletter-header p {
              color: rgba(255, 255, 255, 0.6);
              font-size: 0.9rem;
              line-height: 1.6;
              margin: 0;
            }

            .newsletter-form {
              display: flex;
              flex-direction: column;
              gap: 0.85rem;
            }

            .form-field input {
              width: 100%;
              padding: 0.9rem 1.1rem;
              background: rgba(255, 255, 255, 0.06);
              border: 1px solid #333;
              border-radius: 8px;
              color: white;
              font-size: 0.95rem;
              outline: none;
              transition: border-color 0.2s;
            }

            .form-field input:focus {
              border-color: #C72631;
            }

            .form-field input::placeholder {
              color: rgba(255, 255, 255, 0.3);
            }

            .newsletter-submit {
              width: 100%;
              padding: 1rem;
              background: #C72631;
              color: white;
              border: none;
              border-radius: 8px;
              font-size: 1rem;
              font-weight: 700;
              cursor: pointer;
              transition: all 0.3s;
              margin-top: 0.25rem;
            }

            .newsletter-submit:hover:not(:disabled) {
              transform: translateY(-2px);
              background: #a01f28;
            }

            .newsletter-submit:disabled {
              opacity: 0.7;
              cursor: not-allowed;
            }

            .newsletter-error {
              background: rgba(199, 38, 49, 0.1);
              color: #C72631;
              padding: 0.65rem 1rem;
              border-radius: 8px;
              font-size: 0.85rem;
              border: 1px solid rgba(199, 38, 49, 0.2);
            }

            .newsletter-privacy {
              text-align: center;
              color: rgba(255, 255, 255, 0.35);
              font-size: 0.75rem;
              margin: 0.5rem 0 0;
            }

            .newsletter-success {
              text-align: center;
              padding: 1rem 0;
            }

            .success-icon {
              color: #C72631;
              margin-bottom: 1rem;
            }

            .newsletter-success h3 {
              color: white;
              font-size: 1.5rem;
              font-weight: 900;
              margin: 0 0 0.5rem;
            }

            .newsletter-success p {
              color: rgba(255, 255, 255, 0.6);
              font-size: 0.95rem;
              margin: 0;
              line-height: 1.5;
            }

            @media (max-width: 480px) {
              .newsletter-overlay {
                padding: 0.75rem;
              }

              .newsletter-modal {
                max-height: calc(100dvh - 1.5rem);
                overflow-y: auto;
                padding: 2rem 1.25rem;
              }

              .newsletter-header h3 {
                font-size: 1.4rem;
              }
            }

            @media (max-width: 340px) {
              .newsletter-modal {
                padding: 1.75rem 1rem;
              }


              .newsletter-submit {
                font-size: 0.92rem;
              }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewsletterPopup;
